import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import cn from 'classnames';
import { isBefore, parseISO, startOfDay } from 'date-fns';
import { ReactComponent as WarningIcon } from './assets/warning.svg';
import styles from './NearMixedPairWarning.module.scss';

// Mixed NEAR pairs (plain word + quoted/wildcard/proximity word) only match messages indexed from this date on.
export const NEAR_MIXED_PAIR_DATA_START = new Date('2026-08-25T00:00:00');

const LEARN_MORE_URL =
  'https://docs.osavul.cloud/search-page#3f1d4318dace803bb054f0b3d7a15b9f';

const LANGUAGES_ONLY_KEY =
  'NEAR pairs with <b>{{operators}}</b> don’t include publications in <b>Ukrainian, Chinese, Japanese, Korean, or Thai.</b>';
const LANGUAGES_AND_DATE_KEY =
  'NEAR pairs with <b>{{operators}}</b> don’t include publications in <b>Ukrainian, Chinese, Japanese, Korean, or Thai</b>, or publications collected before <b>Aug 25, 2026.</b>';

interface NearMixedPairWarningProps {
  /** Start of the searched period; `null` means an open/ongoing period, `undefined` means no period at all (date clause skipped). */
  startDate?: Date | string | null;
  isAdvanced?: boolean;
  className?: string;
}

export const NearMixedPairWarning: React.FC<NearMixedPairWarningProps> = ({
  startDate,
  isAdvanced = false,
  className,
}) => {
  const { t } = useTranslation();

  const operators = t(
    isAdvanced
      ? '"keyword", keyword* or keyword]n'
      : '"keyword", keyword* or keyword[n]',
  );
  // Compare calendar days in local time: a date-only string such as '2026-08-25'
  // would otherwise be parsed as UTC midnight and land on Aug 24 west of UTC.
  const coversEarlierData =
    startDate !== undefined &&
    (!startDate ||
      isBefore(
        startOfDay(
          typeof startDate === 'string' ? parseISO(startDate) : startDate,
        ),
        NEAR_MIXED_PAIR_DATA_START,
      ));

  return (
    <div role="status" className={cn(styles.root, className)}>
      <WarningIcon className={styles.icon} aria-hidden="true" />
      <p className={styles.text}>
        <Trans
          i18nKey={
            coversEarlierData ? LANGUAGES_AND_DATE_KEY : LANGUAGES_ONLY_KEY
          }
          values={{ operators }}
          components={{ b: <b /> }}
          tOptions={{ interpolation: { escapeValue: false } }}
        />{' '}
        <a
          href={LEARN_MORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          {t('Learn more')}
        </a>
      </p>
    </div>
  );
};
